import React, { useCallback, useContext, useMemo } from "react";
import type { DropResult } from "react-beautiful-dnd";
import { commitMutation } from "react-relay";

import moment from "moment";
import { graphql } from "relay-runtime";

import { ActionItemsTabContext } from "~/actionItems/ActionItemsTab";
import {
	GROUP_BY_ASSIGNEE,
	GROUP_BY_CUSTOM,
	GROUP_BY_DUE_DATE,
	GROUP_BY_NONE,
	GROUP_BY_STREAM,
	NO_DUE_DATE,
	NO_STREAMS,
	OVERDUE,
	SORT_BY_CUSTOM,
	SORT_BY_DATE_CREATED,
	SORT_BY_DUE_DATE,
	TODAY,
	Views,
} from "~/actionItems/constants";
import { NoteBlockStatus, StatusCondition } from "~/actionItems/enum";
import { ActionItemsContext, ActionItemsRecordingContext } from "~/actionItems/newActionItems/ActionItemsContext";
import type { GroupType } from "~/actionItems/types";
import {
	aiThenRecordingOffsetSort,
	createdAtSort,
	customSort,
	dueDateSort,
	generateSyntheticGroup,
	getKeyForDueDate,
} from "~/actionItems/utils";
import between, { nowUtcMySqlFormat } from "~/lib/between";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { environment } from "~/models";

import ActionItemGroupsRenderer from "./ActionItemGroupsRenderer";
import {
	SYNTHETIC_AI_SUGGESTIONS_GROUP_NAME,
	useShouldShowAISuggestions,
	wasCreatedWithinLastNDays,
} from "./aiSuggestionsUtils";

import type { SyntheticActionItemGroupsListChangeDueDateMutation } from "~/__generated__/SyntheticActionItemGroupsListChangeDueDateMutation.graphql";
import type { SyntheticActionItemGroupsListChangeMultipleDueDateMutation } from "~/__generated__/SyntheticActionItemGroupsListChangeMultipleDueDateMutation.graphql";
import type { SyntheticActionItemGroupsListMoveItemMutation } from "~/__generated__/SyntheticActionItemGroupsListMoveItemMutation.graphql";
import type { SyntheticActionItemGroupsListMoveMultipleItemMutation } from "~/__generated__/SyntheticActionItemGroupsListMoveMultipleItemMutation.graphql";
import type { SyntheticActionItemGroupsListQuery } from "~/__generated__/SyntheticActionItemGroupsListQuery.graphql";

type SyntheticGroupActionItem = SyntheticActionItemGroupsListQuery["response"]["actionItems"]["edges"][number];

const syntheticActionItemGroupsListQuery = graphql`
	query SyntheticActionItemGroupsListQuery(
		$cursor: String
		$count: Int = 1000
		$includeCompleted: Boolean
		$includeWontDo: Boolean
		$includeOnlyCreatedByYou: Boolean
		$includeRecentOnly: Boolean
		$includeAiDetected: Boolean
		$teammatesView: Boolean
		$assignedTo: [ID!]
		$forStream: ID
		$forNote: ID
		$forRecording: ID
	) {
		actionItems(
			first: $count
			after: $cursor
			includeCompleted: $includeCompleted
			includeWontDo: $includeWontDo
			includeOnlyCreatedByYou: $includeOnlyCreatedByYou
			includeRecentOnly: $includeRecentOnly
			includeAiDetected: $includeAiDetected
			teammatesView: $teammatesView
			assignedTo: $assignedTo
			forStream: $forStream
			forNote: $forNote
			forRecording: $forRecording
		) @connection(key: "SyntheticActionItemGroupsList_actionItems") {
			edges {
				node {
					...ActionItemData @relay(mask: false)
				}
			}
		}
	}
`;

const moveActionItemMutation = graphql`
	mutation SyntheticActionItemGroupsListMoveItemMutation(
		$noteBlockId: ID!
		$orderInsertedBefore: String
		$orderInsertedAfter: String
	) @raw_response_type {
		moveActionItem(
			noteBlockId: $noteBlockId
			orderInsertedBefore: $orderInsertedBefore
			orderInsertedAfter: $orderInsertedAfter
		) {
			updatedBlock {
				id
				userOrder
			}
		}
	}
`;

const multipleMoveActionItemMutation = graphql`
	mutation SyntheticActionItemGroupsListMoveMultipleItemMutation(
		$noteBlockIds: [ID!]!
		$orderInsertedBefore: String
		$orderInsertedAfter: String
	) @raw_response_type {
		moveActionItems(
			noteBlockIds: $noteBlockIds
			orderInsertedBefore: $orderInsertedBefore
			orderInsertedAfter: $orderInsertedAfter
		) {
			updatedBlocks {
				id
				userOrder
			}
		}
	}
`;

const changeActionItemDueDateMutation = graphql`
	mutation SyntheticActionItemGroupsListChangeDueDateMutation($noteBlockId: ID!, $dueDate: Date) @raw_response_type {
		changeActionItemDueDate(noteBlockId: $noteBlockId, dueDate: $dueDate) {
			updatedBlock {
				id
				dueDate
			}
		}
	}
`;

const changeMultipleActionItemDueDateMutation = graphql`
	mutation SyntheticActionItemGroupsListChangeMultipleDueDateMutation($noteBlockIds: [ID!]!, $dueDate: Date)
	@raw_response_type {
		changeMultipleActionItemDueDate(noteBlockIds: $noteBlockIds, dueDate: $dueDate) {
			updatedBlocks {
				id
				dueDate
			}
		}
	}
`;
// Optional options prop for the SyntheticActionItemGroupsList component to allow for custom fetch key
// This is useful when we want to force a refetch of the data
// For example when we have a new action item created

type SyntheticActionItemGroupsListProps = {
	fetchKey?: string;
};
export default function SyntheticActionItemGroupsList(props: SyntheticActionItemGroupsListProps) {
	const { view, filters, selectedActionItems, viewer } = useContext(ActionItemsContext);
	const user = viewer?.user;
	const { streamId } = useContext(ActionItemsTabContext);
	const { noteId, recordingId } = useContext(ActionItemsRecordingContext);
	const { groupBy, sortBy, showCompleted, showWontDo, showCreatedByYou, showRecentOnly, assignedTo, search } = filters;

	const data = useLazyLoadQuery<SyntheticActionItemGroupsListQuery>(
		syntheticActionItemGroupsListQuery,
		{
			includeCompleted: showCompleted,
			includeWontDo: showWontDo,
			teammatesView: view === Views.TEAMMATES,
			assignedTo: view !== Views.TEAMMATES ? undefined : assignedTo?.map(assignee => assignee.user?.id || ""),
			forStream: view !== Views.STREAM_TAB ? undefined : streamId,
			forNote: view !== Views.RECORDING ? undefined : noteId,
			forRecording: view !== Views.RECORDING ? undefined : recordingId,
			includeAiDetected: filters.showAIDetected,
			includeOnlyCreatedByYou: view !== Views.TEAMMATES ? undefined : showCreatedByYou,
			includeRecentOnly: view !== Views.TEAMMATES ? undefined : showRecentOnly,
		},
		{
			fetchPolicy: "store-and-network",
			...(props.fetchKey ? { fetchKey: props.fetchKey } : {}),
		},
	);

	const shouldShowAiSuggestions = useShouldShowAISuggestions();

	const [aiDetectedUnacceptedActionItems, actionItems] = React.useMemo(() => {
		// Update the AI Suggestions list and the action items to be shown in groups according to the filters and
		// conditions specified here.

		const aiSuggestions: SyntheticGroupActionItem[] = [];
		const filteredActionItems: SyntheticGroupActionItem[] = [];

		// Keep track of original and carried forward blocks so that we don't see duplicates.
		const actionItemsIdsSet = new Set();

		// It's not ideal to do two passes to accomplish this, but we want to make sure that all the original blocks are
		// added to this set first, without modifying the order of the action items (in case custom sorting is applied).
		data.actionItems.edges.forEach(item => {
			if (!item?.node || item.node.hasOriginalBlock) return;
			actionItemsIdsSet.add(item.node.id);
		});

		data.actionItems.edges.forEach(item => {
			if (!item || !item.node) {
				return;
			}

			if (item.node.originalBlock?.id) {
				// If the item is carried forward, check if its original block already exists on this page.
				// If not, add it to the actionItemIdsSet for future comparison.
				if (actionItemsIdsSet.has(item.node.originalBlock.id)) {
					// The original block is already shown on this page, so we don't want to show this one
					return;
				}
			}

			let viewCondition = true;
			let completedForUser = item.node.done;
			let wontDoForUser = item.node.wontDo;
			if (view === Views.TEAMMATES) {
				if (!item.node.assignments) {
					viewCondition = false;
				} else {
					viewCondition =
						item.node.assignments.length > 0 &&
						(item.node.assignments.length > 1 || item.node.assignments?.[0].assignee.id !== user?.id);
				}
			} else if (view === Views.USER) {
				viewCondition =
					!Boolean(item.node.assignments?.length) ||
					Boolean(item.node.assignments?.some(assignment => assignment.assignee.id === user?.id));
				if (item.node.statusCondition === StatusCondition.ALL && item.node.assignments) {
					for (let i = 0; i < item.node.assignments.length; i++) {
						if (item.node.assignments[i].assignee.id !== user?.id) continue;
						completedForUser = item.node.assignments[i].status === NoteBlockStatus.DONE;
						wontDoForUser = item.node.assignments[i].status === NoteBlockStatus.WONT_DO;
						break;
					}
				}
			}
			const completedCondition = !showCompleted ? !completedForUser : true;
			const wontDoCondition = !showWontDo ? !wontDoForUser : true;
			const searchCondition = !search
				? true
				: item.node.text.toLowerCase().includes(search) ||
					item.node.note?.stream.displayName.toLowerCase().includes(search);

			const shouldShowActionItem = completedCondition && wontDoCondition && searchCondition && viewCondition;

			if (!shouldShowActionItem) {
				return;
			}

			// For an AI-generated action item to show in the AI Suggestions section, it must:
			// - Not exist in another user-defined group (only exists in default "Inbox" group)
			// - Have been created in the last 2 weeks
			// - Be assigned to the current user (should be automatic in USER view)
			// - Not be done or archived
			if (shouldShowAiSuggestions && item.node.aiGenerated) {
				const createdWithinLastTwoWeeks = wasCreatedWithinLastNDays(item.node.createdAt, 14);

				// Only include items from the last 2 weeks.
				// Older unaccepted items will not be shown on the page at all.
				if (createdWithinLastTwoWeeks) {
					aiSuggestions.push(item);
				} else {
					return;
				}
			} else {
				filteredActionItems.push(item);
			}

			// If the item is carried forward and the earlier actionItemsIdsSet.has(id) check didn't return early,
			// and we want to display this action item on the page, then add this ID to the set so that any other
			// carried-forward copies aren't shown as well.
			if (item.node.originalBlock?.id) {
				actionItemsIdsSet.add(item.node.originalBlock.id);
			}
		});

		return [aiSuggestions, filteredActionItems];
	}, [data.actionItems.edges, search, shouldShowAiSuggestions, showCompleted, showWontDo, user?.id, view]);

	const aiSuggestionsGroup: GroupType | null = useMemo(() => {
		if (!shouldShowAiSuggestions || !aiDetectedUnacceptedActionItems.length) return null;
		return {
			...generateSyntheticGroup(SYNTHETIC_AI_SUGGESTIONS_GROUP_NAME),
			items: {
				edges: aiDetectedUnacceptedActionItems,
			},
		};
	}, [shouldShowAiSuggestions, aiDetectedUnacceptedActionItems]);

	const groups: Array<GroupType> = useMemo(() => {
		// When viewing the AI detected action items in the recording view,
		// we always want to show the action items without grouping and
		// sort them by timestamp.
		if (view === Views.RECORDING) {
			actionItems.sort(aiThenRecordingOffsetSort);
			return [
				{
					...generateSyntheticGroup("All action items"),
					items: {
						edges: actionItems,
					},
				},
			];
		}

		let sortingFunction = customSort;
		if (sortBy === SORT_BY_DUE_DATE) {
			sortingFunction = dueDateSort;
		} else if (sortBy === SORT_BY_DATE_CREATED) {
			sortingFunction = createdAtSort;
		}
		actionItems.sort(sortingFunction);

		if (groupBy === GROUP_BY_DUE_DATE) {
			if (!actionItems) {
				return [generateSyntheticGroup("Today")];
			}

			const today = new Date();
			const todayFormattedDate = moment(today).format("YYYY-MM-DD");

			const tomorrow = new Date();
			tomorrow.setDate(tomorrow.getDate() + 1);
			const tomorrowKey = moment(tomorrow).format("dddd, MMM D");
			const tomorrowName = `Tomorrow, ${moment(tomorrow).format("MMM D")}`;
			const tomorrowFormattedDate = moment(tomorrow).format("YYYY-MM-DD");

			const groupsDictionary: { [key: string]: GroupType } = {
				[NO_DUE_DATE]: generateSyntheticGroup("No due dates", NO_DUE_DATE),
				[OVERDUE]: generateSyntheticGroup("Overdue", OVERDUE),
				[TODAY]: generateSyntheticGroup("Today", todayFormattedDate),
				[tomorrowKey]: generateSyntheticGroup(tomorrowName, tomorrowFormattedDate),
			};
			actionItems.forEach(actionItem => {
				if (!actionItem) return;
				const key = getKeyForDueDate(actionItem.node.dueDate, todayFormattedDate);
				if (!groupsDictionary[key]) {
					groupsDictionary[key] = generateSyntheticGroup(key, actionItem.node.dueDate ?? undefined);
				}
				// @ts-ignore
				groupsDictionary[key].items.edges = [...groupsDictionary[key].items.edges, actionItem];
			});
			const dueDateGroups: Array<GroupType> = [];
			const groupKeys = Object.keys(groupsDictionary);
			groupKeys.forEach(groupKey => {
				// Today should always be present
				if (groupsDictionary[groupKey].items.edges.length > 0 || groupKey === TODAY || groupKey === tomorrowKey) {
					dueDateGroups.push(groupsDictionary[groupKey]);
				}
			});
			return dueDateGroups;
		}

		if (groupBy === GROUP_BY_STREAM) {
			if (!actionItems) {
				return [generateSyntheticGroup("No series")];
			}
			const groupsDictionary: { [key: string]: GroupType } = {
				[NO_STREAMS]: generateSyntheticGroup("No series"),
			};
			actionItems.forEach(actionItem => {
				if (!actionItem?.node) return;
				let key = NO_STREAMS;
				if (!actionItem.node.note?.stream.isTodoStream && actionItem.node.note?.stream.displayName) {
					key = actionItem.node.note?.stream.id;
				}
				if (!groupsDictionary[key]) {
					groupsDictionary[key] = generateSyntheticGroup(actionItem.node.note?.stream.displayName, key);
				}
				// @ts-ignore
				groupsDictionary[key].items.edges = [...groupsDictionary[key].items.edges, actionItem];
			});
			const streamGroups: Array<GroupType> = [];
			const groupKeys = Object.keys(groupsDictionary);
			groupKeys.forEach(groupKey => {
				if (groupsDictionary[groupKey].items.edges.length > 0) {
					streamGroups.push(groupsDictionary[groupKey]);
				}
			});
			return streamGroups;
		}

		if (groupBy === GROUP_BY_ASSIGNEE) {
			if (!actionItems) {
				return [];
			}
			const groupsDictionary: { [key: string]: GroupType } = {};
			actionItems.forEach(actionItem => {
				const assignments = actionItem?.node.assignments || [];
				// Do not show if the assignees array is empty or if the viewer
				// Is the only assignee (this should never happen because we filter this scenario
				// but we should check here)
				if (
					assignments.length === 0 ||
					(view === Views.TEAMMATES && assignments.length === 1 && assignments[0].assignee.id === user?.id)
				)
					return;
				assignments.forEach(assignment => {
					const { assignee } = assignment;
					// For action items with multiple assignees we might have the viewer here
					// And we don't want to create a group for them
					if (view === Views.TEAMMATES && assignee.id === user?.id) return;
					const key = assignee.id;
					if (!groupsDictionary[key]) {
						groupsDictionary[key] = generateSyntheticGroup(assignee.fullName, key, assignee);
					}
					// @ts-ignore
					groupsDictionary[key].items.edges = [...groupsDictionary[key].items.edges, actionItem];
				});
			});
			const assigneeGroups: Array<GroupType> = [];
			const groupKeys = Object.keys(groupsDictionary);
			groupKeys.forEach(groupKey => {
				if (groupsDictionary[groupKey].items.edges.length > 0) {
					assigneeGroups.push(groupsDictionary[groupKey]);
				}
			});
			const directReportsSet = new Set();
			viewer?.user.internalAttributes?.directReports?.edges.map(edge => {
				directReportsSet.add(edge.node.id);
			});
			// Sort groups first in the following order manager -> direct reports -> alphabetically by assignee's name
			assigneeGroups.sort((a: GroupType, b: GroupType) => {
				if (a.assignee?.id === viewer?.user.internalAttributes?.manager?.id) return -1;
				if (b.assignee?.id === viewer?.user.internalAttributes?.manager?.id) return 1;
				if (directReportsSet.has(a.assignee?.id)) {
					if (directReportsSet.has(b.assignee?.id)) {
						if ((a.assignee?.fullName || "zzz") > (b.assignee?.fullName || "zzz")) return 1;
						return -1;
					}
					return -1;
				}
				if (directReportsSet.has(b.assignee?.id)) return 1;
				if ((a.assignee?.fullName || "zzz") > (b.assignee?.fullName || "zzz")) return 1;
				return -1;
			});
			// If a filtered assignee is not present we will add it as an empty group
			assignedTo?.forEach(assignee => {
				if (!assignee.user?.id || groupsDictionary[assignee.user.id]) return;
				assigneeGroups.push(generateSyntheticGroup(assignee.user.fullName, assignee.user.id, assignee.user));
			});
			return assigneeGroups;
		}

		// Group by None
		return [
			{
				...generateSyntheticGroup("All action items"),
				items: {
					edges: actionItems,
				},
			},
		];
	}, [
		view,
		sortBy,
		actionItems,
		groupBy,
		viewer?.user.internalAttributes?.directReports?.edges,
		viewer?.user.internalAttributes?.manager?.id,
		assignedTo,
		user?.id,
	]);

	const onDropEnd = useCallback(
		({ destination, draggableId, source }: DropResult) => {
			// Group by should never be CUSTOM or STREAM
			if (!destination || groupBy === GROUP_BY_CUSTOM || groupBy === GROUP_BY_STREAM) {
				return;
			}

			if (groupBy === GROUP_BY_NONE) {
				// We only allow dragging and dropping on the NONE group
				// If the sorting is custom
				if (sortBy !== SORT_BY_CUSTOM || destination.index === source.index || groups.length === 0) {
					return;
				}
				// None grouping will always have just one group
				const noneGroup = groups[0];

				// If we are moving upwards we push the current index downwards
				// While if we are moving downwards we push the current index upwards
				// And this matter when we are getting the orders here
				let orderInsertedBefore: string | undefined;
				let orderInsertedAfter: string | undefined;
				if (destination.index > source.index) {
					// Pushing upwards
					if (destination.index < noneGroup.items.edges.length - 1) {
						orderInsertedBefore = noneGroup.items.edges[destination.index + 1]?.node.userOrder ?? undefined;
					}
					if (destination.index < noneGroup.items.edges.length) {
						orderInsertedAfter = noneGroup.items.edges[destination.index]?.node.userOrder ?? undefined;
					}
				} else {
					// Pushing downwards
					if (destination.index < noneGroup.items.edges.length) {
						orderInsertedBefore = noneGroup.items.edges[destination.index]?.node.userOrder ?? undefined;
					}
					if (destination.index > 0) {
						orderInsertedAfter = noneGroup.items.edges[destination.index - 1]?.node.userOrder ?? undefined;
					}
				}
				// Single drag and drop
				if (selectedActionItems.length <= 1) {
					const newOrder = between(orderInsertedBefore || " ", orderInsertedAfter || nowUtcMySqlFormat());

					commitMutation<SyntheticActionItemGroupsListMoveItemMutation>(environment, {
						mutation: moveActionItemMutation,
						variables: {
							noteBlockId: draggableId,
							orderInsertedBefore,
							orderInsertedAfter,
						},
						optimisticResponse: {
							moveActionItem: {
								updatedBlock: {
									id: draggableId,
									userOrder: newOrder,
								},
							},
						},
					});
					return;
				}
				// Multi drag and drop
				const items = selectedActionItems.map(item => ({ node: item }));
				items.sort(customSort);
				const ids = items.map(item => item.node.id);
				const updatedBlocks: Array<{ id: string; userOrder: string }> = [];
				let currentInsertedAfter = orderInsertedAfter;
				ids.forEach(id => {
					const newOrder = between(orderInsertedBefore || " ", currentInsertedAfter || nowUtcMySqlFormat());
					updatedBlocks.push({
						id,
						userOrder: newOrder,
					});
					currentInsertedAfter = newOrder;
				});

				commitMutation<SyntheticActionItemGroupsListMoveMultipleItemMutation>(environment, {
					mutation: multipleMoveActionItemMutation,
					variables: {
						noteBlockIds: ids,
						orderInsertedBefore,
						orderInsertedAfter,
					},
					optimisticResponse: {
						moveActionItems: {
							updatedBlocks,
						},
					},
				});
				return;
			}

			// You cannot move to overdue or AI Suggestions
			if (destination.droppableId === OVERDUE || destination.droppableId === SYNTHETIC_AI_SUGGESTIONS_GROUP_NAME) {
				return;
			}

			const dueDate = destination.droppableId === NO_DUE_DATE ? undefined : destination.droppableId;

			// Single drag and drop
			if (selectedActionItems.length <= 1) {
				commitMutation<SyntheticActionItemGroupsListChangeDueDateMutation>(environment, {
					mutation: changeActionItemDueDateMutation,
					variables: {
						noteBlockId: draggableId,
						dueDate,
					},
					optimisticResponse: {
						changeActionItemDueDate: {
							updatedBlock: {
								id: draggableId,
								dueDate: dueDate || null,
							},
						},
					},
				});
				return;
			}

			// Multi drag and drop
			const ids = selectedActionItems.map(item => item.id);
			const updatedBlocks = ids.map(id => ({ id, dueDate: dueDate || null }));
			commitMutation<SyntheticActionItemGroupsListChangeMultipleDueDateMutation>(environment, {
				mutation: changeMultipleActionItemDueDateMutation,
				variables: {
					noteBlockIds: ids,
					dueDate,
				},
				optimisticResponse: {
					changeMultipleActionItemDueDate: {
						updatedBlocks,
					},
				},
			});
			return;
		},
		[groups, groupBy, sortBy, selectedActionItems],
	);

	return (
		<ActionItemGroupsRenderer
			groups={shouldShowAiSuggestions && aiSuggestionsGroup ? [aiSuggestionsGroup, ...groups] : groups}
			onDropEnd={onDropEnd}
		/>
	);
}
